import { Fragment } from 'react';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Hooks
import usePagination from '../../../hooks/usePagination';

// Styles
import { table_container_base } from '../../../static/styles';

const statusConfig = {
  'Active': { icon: CircleIcon, color: '#4caf50', bg: '#e8f5e9' },
  'In Progress': { icon: CircleIcon, color: '#ff9800', bg: '#fff3e0' },
  'Completed': { icon: CheckCircleIcon, color: '#4caf50', bg: '#e8f5e9' },
  'Cancelled': { icon: CancelIcon, color: '#f44336', bg: '#ffebee' },
};

const MobileAppointmentCard = ({ appointment }) => {
  const status = statusConfig[appointment.status] || statusConfig['Active'];
  const StatusIcon = status.icon;
  
  return (
    <Card variant="outlined" sx={{ mb: 2, borderLeft: `4px solid ${status.color}` }}>
      <Link href={`/appointments/${appointment.id}`} underline="none">
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Chip 
              icon={<StatusIcon sx={{ fontSize: 14 }} />}
              label={appointment.status} 
              size="small" 
              sx={{ bgcolor: status.bg, color: status.color, fontWeight: 600 }} 
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EventIcon sx={{ fontSize: 16, color: '#666' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {moment(appointment.startTime).format('MM/DD/YY')}
              </Typography>
            </Box>
          </Box>
          
          <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
            {appointment.type?.name || 'No type'}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: '#757575' }} />
            <Typography variant="body2" sx={{ flex: 1, color: '#333' }}>
              {appointment.property.address}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 18, color: '#999' }} />
          </Box>
          
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Created by {appointment.createdBy?.fullName || 'Unknown'}
          </Typography>
        </CardContent>
      </Link>
    </Card>
  );
};

const ClientAppointmentsTable = ({ appointments }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  return (
    <Fragment>
      {/* Desktop Table */}
      {!isMobile && (
        <TableContainer component={Paper} sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Status</TableCell>
                <TableCell width="15%">Date</TableCell>
                <TableCell width="15%">Type</TableCell>
                <TableCell width="30%">Property</TableCell>
                <TableCell width="20%">Created By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {appointments.length ? (
                appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
                  <TableRow key={appointment.id}>
                    <TableCell>
                      <Link href={`/appointments/${appointment.id}`} sx={{ fontWeight: 'bold' }} className="full-col-flex-link" underline="none" color="rgba(0,0,0,0.87)">
                        {appointment.status === 'Active' && <><CircleIcon sx={{ color: 'green', mr: 1, width: 15 }} /> Active</>}
                        {appointment.status === 'In Progress' && <><CircleIcon sx={{ color: 'orange', mr: 1, width: 15 }} /> In Progress</>}
                        {appointment.status === 'Completed' && <><CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15 }} /> Completed</>}
                        {appointment.status === 'Cancelled' && <><CancelIcon sx={{ color: 'red', mr: 1, width: 15 }} /> Cancelled</>}
                      </Link>
                    </TableCell>
                    <TableCell><Link href={`/appointments/${appointment.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{moment(appointment.startTime).format('MM/DD/YYYY')}</Link></TableCell>
                    <TableCell><Link href={`/appointments/${appointment.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{appointment.type?.name || '-'}</Link></TableCell>
                    <TableCell><Link href={`/appointments/${appointment.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{appointment.property.address}</Link></TableCell>
                    <TableCell><Link href={`/appointments/${appointment.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{appointment.createdBy?.fullName || 'Unknown'}</Link></TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center' }}><strong>No Data</strong></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile Cards */}
      {isMobile && (
        <Box>
          {appointments.length ? (
            appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
              <MobileAppointmentCard key={appointment.id} appointment={appointment} />
            ))
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 3 }}>No appointments</Typography>
          )}
        </Box>
      )}

      {!isMobile && (
        <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={appointments.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Per page" />
      )}

      {isMobile && appointments.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page - 1)} disabled={page === 0}>Prev</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>{page + 1}/{Math.ceil(appointments.length / rowsPerPage)}</Typography>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page + 1)} disabled={(page + 1) * rowsPerPage >= appointments.length}>Next</Button>
        </Box>
      )}
    </Fragment>
  );
};

export default ClientAppointmentsTable;
